/**
 * LmsQuizResult — shown after submitting a quiz attempt.
 */
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Award, Home } from "lucide-react";

export default function LmsQuizResult() {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [result, setResult] = useState(location.state?.result || null);
  const [attempt, setAttempt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/lms/attempts/${attemptId}`);
        setAttempt(data);
      } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    })();
  }, [activeWorkspaceId, attemptId]);

  if (!result && !attempt) return <div className="p-6">Loading result…</div>;
  const r = result || {
    score_percent: attempt.score_percent, pass: attempt.pass,
    per_question: attempt.per_question,
    correct_answers: {}, pass_percent: 70,
  };

  return (
    <div className="min-h-screen bg-muted/20 p-6">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className={`border-2 p-6 text-center ${r.pass ? "border-emerald-500 bg-emerald-50/40 dark:bg-emerald-900/10" : "border-rose-500 bg-rose-50/40 dark:bg-rose-900/10"}`} data-testid="lms-quiz-result-card">
          {r.pass ? (
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
          ) : (
            <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-2" />
          )}
          <h1 className="text-2xl font-semibold">{r.pass ? "You passed!" : "Not this time"}</h1>
          <div className="text-4xl font-bold mt-2 font-mono">{r.score_percent}%</div>
          <div className="text-sm text-muted-foreground mt-1">Threshold: {r.pass_percent || 70}%</div>
          {attempt?.status?.startsWith("auto_") && (
            <div className="mt-2 text-xs text-amber-600">
              {attempt.status === "auto_submitted_violation" ? "Auto-submitted due to violations" : "Auto-submitted at timeout"}
            </div>
          )}
        </div>

        {r.per_question && (
          <div className="border border-border p-4 space-y-2">
            <div className="text-[10px] uppercase font-semibold text-muted-foreground">Answer review</div>
            {r.per_question.map((pq, i) => (
              <div key={pq.question_id} className="text-xs border-l-2 pl-2" style={{ borderColor: pq.correct ? "#10b981" : "#f43f5e" }}>
                <span className="font-mono text-muted-foreground">#{i + 1}</span>
                <span className={`ml-1 ${pq.correct ? "text-emerald-600" : "text-rose-600"}`}>
                  {pq.correct ? "Correct" : "Incorrect"} · {pq.points_earned}/{pq.points_possible} pts
                </span>
                <div className="text-muted-foreground">Given: {Array.isArray(pq.given) ? pq.given.join(", ") : String(pq.given ?? "—")}</div>
                {r.correct_answers?.[pq.question_id] && !pq.correct && (
                  <div className="text-emerald-600">
                    Answer: {(r.correct_answers[pq.question_id].correct_answers || []).join(", ") || r.correct_answers[pq.question_id].correct_text || "—"}
                  </div>
                )}
                {r.correct_answers?.[pq.question_id]?.explanation && (
                  <div className="text-muted-foreground italic">{r.correct_answers[pq.question_id].explanation}</div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="rounded-none flex-1" onClick={() => navigate("/app/lms/my-learning")}>
            <Home className="w-4 h-4 mr-1" /> Back to my learning
          </Button>
          {r.pass && (
            <Button className="rounded-none flex-1" onClick={() => navigate("/app/leaderboard")}>
              <Award className="w-4 h-4 mr-1" /> View leaderboard
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
